import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { StepCard } from '../../../../../PulseCheck.styles';
import { cardAnimationVariants } from '../../../../../../BeFi20/BeFi20.meta';
import { useSetGoalContext } from '../../../../context/useSetGoalContext';
import { FormValues, validation } from './Expense.meta';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { InputNumberMask } from '../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { SavePeriod } from '../../../../context/SetGoalProvider';
import { STARTS_TODAY } from '../../../../utils/getDisplayDate';
import { FORM_ID } from '../../../../SetGoal.meta';
import { StepWrapper } from '../../../../../components/StepWrapper/StepWrapper';

export const Expense = () => {
  const { wizard, setWizard, onSuccess, options } = useSetGoalContext();

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: wizard.expense,
    resolver: yupResolver(validation),
  });

  const onSubmit = handleSubmit(values => {
    const newWizard = { ...wizard, expense: values };

    setWizard(newWizard);

    onSuccess(newWizard);
  });

  const expenseEvery = watch('saveEvery');

  const saveEveryOptions = [
    { label: 'Month', value: SavePeriod.Monthly },
    { label: 'Year', value: SavePeriod.Yearly },
  ];

  const savePeriodOptions = [
    { label: 'Month(s)', value: SavePeriod.Monthly },
    { label: 'Year(s)', value: SavePeriod.Yearly },
  ];

  return (
    <StepWrapper as="form" id={FORM_ID} onSubmit={onSubmit}>
      <StepCard variants={cardAnimationVariants} initial="enter" animate="visible">
        <Text as="h1" fontSize="h4" fontWeight={600} mb="16">
          How much do you want to spend?
        </Text>

        <Controller
          control={control}
          name="amount"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Amount"
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              formVariant={errors.amount && 'error'}
              helperText={errors.amount?.message}
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              {...field}
            />
          )}
        />

        <Text variant="formLabel" mt="16" mb="4">
          Frequency
        </Text>

        <Box display="flex" alignItems="flex-start" css="gap: 16px">
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <Select
                innerRef={field.ref}
                label="Starting"
                value={
                  typeof field.value === 'number'
                    ? {
                        value: field.value,
                        label: field.value === STARTS_TODAY ? 'Starts Today' : field.value,
                      }
                    : null
                }
                options={[
                  { label: 'Starts Today', value: STARTS_TODAY },
                  ...options.YearsOptions.map(year => ({ label: year, value: year })),
                ]}
                onChange={option => field.onChange(option?.value)}
                formVariant={errors.startDate && 'error'}
                helperText={errors.startDate?.message}
                css="flex: 1"
              />
            )}
          />

          <Controller
            name="saveEvery"
            control={control}
            render={({ field }) => (
              <Select
                innerRef={field.ref}
                label="Every"
                value={saveEveryOptions.find(({ value }) => field.value === value)}
                options={saveEveryOptions}
                onChange={option => {
                  field.onChange(option?.value);

                  // Only period years are allowed when expenseEvery is set to years
                  if (option?.value === SavePeriod.Yearly) {
                    setValue('saveForPeriod', SavePeriod.Yearly, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
                formVariant={errors.saveEvery && 'error'}
                helperText={errors.saveEvery?.message}
                css="flex: 1"
              />
            )}
          />

          <Controller
            control={control}
            name="saveForLength"
            render={({ field }) => (
              <InputNumberMask
                label="For"
                value={field.value}
                allowNegative={false}
                onValueChange={({ floatValue }) => field.onChange(floatValue ?? null)}
                formVariant={errors.saveForLength && 'error'}
                helperText={errors.saveForLength?.message}
                css="width: 90px"
              />
            )}
          />

          <Controller
            name="saveForPeriod"
            control={control}
            render={({ field }) => (
              <Select
                innerRef={field.ref}
                label="Period"
                value={savePeriodOptions.find(({ value }) => field.value === value)}
                options={savePeriodOptions}
                onChange={option => field.onChange(option?.value)}
                disabled={expenseEvery === SavePeriod.Yearly}
                formVariant={errors.saveForPeriod && 'error'}
                helperText={errors.saveForPeriod?.message}
                css="flex: 1"
              />
            )}
          />
        </Box>
      </StepCard>
    </StepWrapper>
  );
};
